import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Mail, Check, AlertCircle } from 'lucide-react';
import './PasswordReset.css';

import { API as API_URL } from '../utils/apiConfig';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [mockToken, setMockToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/password/forgot`, {
        email
      });

      setSuccess(true);
      if (response.data.mock_token) {
        setMockToken(response.data.mock_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="password-reset-container">
      <div className="password-reset-card">
        {!success ? (
          <>
            <div className="reset-header">
              <Mail size={48} className="reset-icon" />
              <h2>Passwort vergessen?</h2>
              <p>Gib deine Email ein und wir senden dir einen Reset-Link</p>
            </div>

            <form onSubmit={handleSubmit} className="reset-form">
              <div className="form-group">
                <label>Email-Adresse</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="max@example.com"
                  required
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="reset-button"
                disabled={loading}
              >
                {loading ? 'Wird gesendet...' : 'Reset-Link senden'}
              </button>

              <a href="/login" className="back-to-login">
                Zurück zum Login
              </a>
            </form>
          </>
        ) : (
          <div className="reset-success">
            <div className="success-icon">
              <Check size={64} />
            </div>
            <h2>Email versendet!</h2>
            <p>
              Wenn ein Account mit dieser Email existiert, haben wir dir einen Reset-Link gesendet.
              Überprüfe dein Postfach (und Spam-Ordner).
            </p>

            {mockToken && (
              <div className="mock-token-info">
                <AlertCircle size={18} />
                <div>
                  <strong>Test-Modus:</strong>
                  <br />
                  <a href={`/reset-password?token=${mockToken}`} className="mock-link">
                    Klicke hier zum Passwort zurücksetzen
                  </a>
                </div>
              </div>
            )}

            <a href="/login" className="back-button">
              Zurück zum Login
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
      return;
    }

    // Validate token
    const validateToken = async () => {
      try {
        const response = await axios.post(`${API_URL}/password/validate-token`, null, {
          params: { token }
        });
        setTokenValid(response.data.valid);
        if (!response.data.valid) {
          setError(response.data.message);
        }
      } catch (err) {
        setError('Ungültiger oder abgelaufener Token');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/password/reset`, {
        token,
        new_password: password
      });

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Passwort zurücksetzen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-card">
          <div className="loading-spinner">Validiere Token...</div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-card">
          <div className="error-state">
            <AlertCircle size={64} className="error-icon" />
            <h2>Token ungültig</h2>
            <p>{error || 'Dieser Reset-Link ist ungültig oder abgelaufen'}</p>
            <a href="/forgot-password" className="retry-button">
              Neuen Link anfordern
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <div className="password-reset-card">
        {!success ? (
          <>
            <div className="reset-header">
              <Lock size={48} className="reset-icon" />
              <h2>Neues Passwort setzen</h2>
              <p>Wähle ein starkes Passwort für deinen Account</p>
            </div>

            <form onSubmit={handleSubmit} className="reset-form">
              <div className="form-group">
                <label>Neues Passwort</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  required
                />
              </div>

              <div className="form-group">
                <label>Passwort bestätigen</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="reset-button"
                disabled={loading}
              >
                {loading ? 'Wird gespeichert...' : 'Passwort zurücksetzen'}
              </button>
            </form>
          </>
        ) : (
          <div className="reset-success">
            <div className="success-icon">
              <Check size={64} />
            </div>
            <h2>Passwort geändert!</h2>
            <p>Dein Passwort wurde erfolgreich zurückgesetzt.</p>
            <p className="redirect-info">Du wirst in 3 Sekunden zum Login weitergeleitet...</p>
          </div>
        )}
      </div>
    </div>
  );
};